import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, IconButton, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useCallback, useState } from 'react';
import * as yup from 'yup';
import { NumericInput } from '../../Input/NumericInput';
import { PensionFormValues } from '../PensionPage.hooks';

const INITIAL_VALUES: PensionFormValues = {
  currentAge: 35,
  expectedRetirementAge: 63,
  averageMonthlyIncome: 3_000_000,
  contributionYears: 25,
};

const schema = yup.object().shape({
  currentAge: yup.number().min(18, '18세 이상을 입력해 주세요.').max(59, '59세 이하를 입력해 주세요.').required('필수 입력 항목입니다.'),
  expectedRetirementAge: yup.number().min(60, '60세 이상이어야 합니다.').max(70, '70세 이하로 입력해 주세요.').moreThan(yup.ref('currentAge'), '수급 나이는 현재 나이보다 커야 합니다.').required('필수 입력 항목입니다.'),
  averageMonthlyIncome: yup.number().min(350_000, '기준소득 하한은 35만 원입니다.').max(6_170_000, '기준소득 상한은 617만 원입니다.').required('필수 입력 항목입니다.'),
  contributionYears: yup.number().min(1, '1년 이상 입력해 주세요.').max(40, '최대 40년입니다.').required('필수 입력 항목입니다.'),
});

function FieldTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);

  return (
    <Tooltip open={open} onClose={() => setOpen(false)} title={text} arrow placement="top">
      <IconButton size="small" onClick={() => setOpen((v) => !v)} sx={{ p: 0.25, color: '#9ca3af' }}>
        <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
    </Tooltip>
  );
}

export interface PensionFormProps {
  onSubmit: (values: PensionFormValues) => void;
  className?: string;
}

export function PensionForm({ onSubmit, className }: PensionFormProps) {
  const handleSubmit = useCallback((values: PensionFormValues) => onSubmit(values), [onSubmit]);

  return (
    <Formik initialValues={INITIAL_VALUES} validationSchema={schema} onSubmit={handleSubmit}>
      {({ errors, setFieldValue }) => (
        <Form className={classNames(className)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Field name="currentAge">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>지금 나이<FieldTooltip text="국민연금 수령 개시 나이(현재 기준 만 63~65세)와의 차이로 남은 납부 기간을 계산합니다." /></span>}
                  placeholder="35"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.currentAge && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.currentAge}</div>}

            <Field name="expectedRetirementAge">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>연금 받기 시작할 나이<FieldTooltip text="수령을 1년 늦출 때마다 약 7.2%씩 더 받습니다. 기본 수령 나이는 만 63세(1969년생 이후)입니다." /></span>}
                  placeholder="63"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.expectedRetirementAge && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.expectedRetirementAge}</div>}

            <Field name="averageMonthlyIncome">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>국민연금 납부 기준 월 소득 (원)<FieldTooltip text="세전 월급 기준입니다. 2024년 기준 상한 617만 원 / 하한 35만 원. 상한을 초과하면 자동으로 상한 기준으로 계산됩니다." /></span>}
                  placeholder="3,000,000"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.averageMonthlyIncome && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.averageMonthlyIncome}</div>}

            <Field name="contributionYears">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>예상 총 납부 기간 (년)<FieldTooltip text="현재까지 납부한 기간 + 앞으로 납부할 기간의 합입니다. 국민연금공단 앱에서 납부 이력을 확인할 수 있습니다." /></span>}
                  placeholder="25"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.contributionYears && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.contributionYears}</div>}
          </div>

          <div style={{ marginTop: '24px' }}>
            <Button variant="contained" type="submit" fullWidth size="large" sx={{ background: '#000033', '&:hover': { background: '#001166' } }}>
              예상 연금 계산하기
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
